import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import "./articleDetails.scss";
import {
  getArticleDetailsAction,
  getOtherArticles
} from "../../actions/articles.action";
import { PATHS } from "../../constant";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { LoaderPage } from "../../component/Loader";
import NoDataFound from "../../component/NoDataFound";
import {
  decodeArticleSlug,
  encodeArticleSlug,
  setArticleNotFound
} from "./articleSlug";
import { sanitizeArticleContent } from "./articleContentSanitizer";

const tags = [];
const ArticleDetailLoader = () => <LoaderPage loading={true} />;
const getArticleDataFromResponse = response => {
  if (response && response.data && !Array.isArray(response.data)) {
    return response.data;
  }

  return response || {};
};

const getArticleTags = article => {
  const articleTags = article.tags || article.tag || article.tagIds || [];

  if (!Array.isArray(articleTags) || !articleTags.length) {
    return tags;
  }

  return articleTags
    .map(tag =>
      typeof tag === "string" || typeof tag === "number"
        ? String(tag)
        : tag && (tag.name || tag.label || tag.title || tag.value)
    )
    .filter(Boolean);
};

const getImageUrl = image => {
  if (!image) return null;
  if (typeof image === "string") return image.trim() || null;

  return image.s3Url || image.url || image.path || image.fileUrl || null;
};

const getArticleImage = article => {
  const articleImages = Array.isArray(article.images) ? article.images : [];
  const firstApiImage = articleImages
    .map(getImageUrl)
    .find(imageUrl => imageUrl);

  if (firstApiImage) {
    return firstApiImage;
  }

  const fallbackImage = Array.isArray(article.image)
    ? article.image.map(getImageUrl).find(imageUrl => imageUrl)
    : getImageUrl(article.image);

  return fallbackImage || null;
};

const formatArticleDate = value => {
  if (!value) return "";

  const rawDate = String(value).trim();
  const ddmmyyyyMatch = rawDate.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  const parsedDate = ddmmyyyyMatch
    ? new Date(
        Number(ddmmyyyyMatch[3]),
        Number(ddmmyyyyMatch[2]) - 1,
        Number(ddmmyyyyMatch[1])
      )
    : new Date(rawDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return rawDate;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const AuthorIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="26"
    height="26"
    viewBox="0 0 26 26"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <g filter="url(#article-details-author-filter)">
      <circle cx="13" cy="9" r="9" fill="#00C195" />
      <circle cx="13" cy="7.5" r="3" stroke="white" />
      <path
        d="M6.5 15.25C6.91667 14.6667 7.9 13.35 8.5 12.75C9.25 12 10.5 12 10.75 12C10.95 12 14.5 12 16.25 12C16.6667 12 17.65 12.15 18.25 12.75C18.85 13.35 19.6667 14.5 20 15"
        stroke="white"
      />
    </g>
    <defs>
      <filter
        id="article-details-author-filter"
        x="0"
        y="0"
        width="26"
        height="26"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.756863 0 0 0 0 0.584314 0 0 0 0.3 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

const ArticleDetailContent = ({ match }) => {
  const [article, setArticle] = useState({});
  const [otherArticles, setOtherArticles] = useState([]);
  const [isArticleLoading, setIsArticleLoading] = useState(true);
  const [contentHeight, setContentHeight] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const contentRef = useRef(null);
  const slug = match && match.params && match.params.id;
  const articleId = decodeArticleSlug(slug);

  useEffect(() => {
    let isMounted = true;
    const markNotFound = () => {
      setIsNotFound(true);
      setArticleNotFound(true); // store in articleSlug
    };
    const fetchArticleDetails = async () => {
      if (!articleId) {
        markNotFound();
        setIsArticleLoading(false);
        return;
      }
      try {
        setIsArticleLoading(true);
        setIsNotFound(false);
        setArticleNotFound(false);

        const response = await getArticleDetailsAction(articleId);
        const data = getArticleDataFromResponse(response);

        if (isMounted) {
          if (data && data.title) {
            setArticle(data);
          } else {
            markNotFound();
          }
        }
      } catch (error) {
        console.error("Error fetching article details:", error);
        if (isMounted) {
          markNotFound();
        }
      } finally {
        if (isMounted) {
          setIsArticleLoading(false);
        }
      }
    };

    fetchArticleDetails();

    return () => {
      isMounted = false;
      setArticleNotFound(false);
    };
  }, [articleId]);

  useEffect(() => {
    const fetchOtherArticles = async () => {
      if (!articleId) return;

      try {
        const response = await getOtherArticles(articleId);
        const articles = Array.isArray(response && response.data)
          ? response.data
          : [];
        // setOtherArticles(articles);
        const filteredArticles = articles.filter(
          item => String(item.id) !== String(articleId)
        );
        setOtherArticles(filteredArticles);
      } catch (error) {
        console.error("Error fetching other articles:", error);
        setOtherArticles([]);
      }
    };

    fetchOtherArticles();
  }, [articleId]);

  const articleTitle = article.title;
  const articleDate = article.createdAt;
  const articleAuthor =
    article.submittedByName || article.submittedBy || article.author;
  const articleTags = getArticleTags(article);
  const articleImage = getArticleImage(article);
  const articleContent = article.articleContent || article.content;
  const articleLinkedinProfileLink =
    article.articleLinkedinProfileLink ||
    article.linkedinProfileLink ||
    article.profileLink ||
    "";
  const hasArticleLinkedinProfileLink = String(
    articleLinkedinProfileLink || ""
  ).trim();

  useLayoutEffect(() => {
    const updateContentHeight = () => {
      if (!contentRef.current) return;
      setContentHeight(contentRef.current.offsetHeight);
    };

    updateContentHeight();
    window.addEventListener("resize", updateContentHeight);

    return () => {
      window.removeEventListener("resize", updateContentHeight);
    };
  }, [articleContent, otherArticles.length]);

  if (isArticleLoading) {
    return <ArticleDetailLoader />;
  }
  return (
    <main
      className="article-details-page"
      style={{ margin: isNotFound ? "auto" : null }}
    >
      {isNotFound ? (
        <div style={{ marginTop: "6.2em" }}>
          <NoDataFound />
        </div>
      ) : (
        <>
          <section className="article-details-hero">
            <h1>{articleTitle}</h1>
            <div className="article-details-meta">
              <span>{formatArticleDate(articleDate)}</span>
              <span className="article-details-meta-divider" />
              {hasArticleLinkedinProfileLink ? (
                <a
                  className="article-details-author"
                  href={hasArticleLinkedinProfileLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AuthorIcon className="article-details-author-icon" />
                  {articleAuthor}
                </a>
              ) : (
                <span className="article-details-author">
                  <AuthorIcon className="article-details-author-icon" />
                  {articleAuthor}
                </span>
              )}
            </div>
            <div className="article-details-tags">
              {articleTags.map(tag => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
            {articleImage && (
              <img
                src={articleImage}
                alt={articleTitle || "Article banner"}
                className="article-details-banner"
                onError={event => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}
          </section>

          <section
            className={`article-details-content-wrap ${
              otherArticles.length === 0 ? "full-width-content" : ""
            }`}
          >
            <article
              ref={contentRef}
              className="article-details-content"
              dangerouslySetInnerHTML={{
                __html: sanitizeArticleContent(articleContent)
              }}
            />

            {otherArticles.length > 0 && (
              <aside
                className="article-details-sidebar"
                style={contentHeight ? { height: contentHeight } : null}
              >
                <h2>Other Articles</h2>
                <div className="article-details-list">
                  {otherArticles.map(article => {
                    const otherArticleImage = getArticleImage(article);

                    return (
                      <a
                        href={`${
                          PATHS.ARTICLES_DETAILS_PAGE
                        }/${encodeArticleSlug(article.id, article.title)}`}
                        className="article-details-item"
                        key={article.id}
                      >
                        {otherArticleImage && (
                          <img
                            src={otherArticleImage}
                            alt={article.title || "Article"}
                            onError={event => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <span>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip
                                // id={`article-details-title-tooltip-${article.id}`}
                                className="mis-tooltip"
                                placement="top"
                              >
                                {article.title}
                              </Tooltip>
                            }
                          >
                            <strong className="article-details-item-title">
                              {article.title}
                            </strong>
                          </OverlayTrigger>
                          <small>
                            {article.createdAt && (
                              <span>{article.createdAt}</span>
                            )}
                          </small>
                        </span>
                      </a>
                    );
                  })}
                </div>
              </aside>
            )}
          </section>
        </>
      )}

      <div className="article-details-footer p-2" />
    </main>
  );
};

const ArticleDetail = props => (
  <Suspense fallback={<ArticleDetailLoader />}>
    <ArticleDetailContent {...props} />
  </Suspense>
);

export default ArticleDetail;
